import type { DailyMenuOrderSelectionResponse } from '../types/orders';
import type { DailyMenuDishSuggestion } from '../types/dailyMenu';

export interface CourseGroup {
  sortOrder: number;
  courseName: string;
  options: string[];
}

export function groupSelectionsByCourse(
  selections?: DailyMenuOrderSelectionResponse[],
): CourseGroup[] {
  if (!selections?.length) return [];
  const map = new Map<number, CourseGroup>();
  for (const s of selections) {
    const cur =
      map.get(s.courseSortOrder) ??
      { sortOrder: s.courseSortOrder, courseName: s.courseName, options: [] };
    cur.options.push(s.optionName);
    map.set(s.courseSortOrder, cur);
  }
  return Array.from(map.values()).sort((a, b) => a.sortOrder - b.sortOrder);
}

/** Marcas de acento que quedan sueltas al descomponer con NFD. */
const DIACRITICS = /[̀-ͯ]/g;

/**
 * Misma llave que `DishNameKey` del backend: minúsculas, sin acentos y sin
 * espacios repetidos. Sirve para no volver a ofrecer un platillo que ya está
 * en el tiempo, aunque se haya escrito distinto.
 */
export function normalizeDishName(name: string | undefined | null): string {
  if (!name) return '';
  return name
    .normalize('NFD')
    .replace(DIACRITICS, '')
    .toLowerCase()
    .replace(/\s+/g, ' ')
    .trim();
}

/** Sugerencia con sus llaves ya calculadas. */
export interface IndexedDishSuggestion {
  suggestion: DailyMenuDishSuggestion;
  nameKey: string;
  courseKey: string;
}

/**
 * Normaliza el recetario **una sola vez**.
 *
 * El filtrado corre por cada renglón del menú en cada tecla que se escribe: si
 * normalizara ahí, serían decenas de miles de `String.normalize` por render con
 * el recetario completo, y eso se siente como lag al teclear en la tablet.
 * Se calcula una vez por lista y el filtro sólo compara cadenas.
 */
export function indexDishSuggestions(
  suggestions: DailyMenuDishSuggestion[],
): IndexedDishSuggestion[] {
  return suggestions
    .map((suggestion) => ({
      suggestion,
      nameKey: normalizeDishName(suggestion.name),
      courseKey: normalizeDishName(suggestion.courseName),
    }))
    .filter((entry) => entry.nameKey !== '');
}

export interface DishSuggestionFilter {
  indexed: IndexedDishSuggestion[];
  /** Tiempo que se está editando: sus platillos se ofrecen primero. */
  courseName?: string;
  /** Lo que lleva tecleado; vacío muestra los más usados. */
  query?: string;
  /** Llaves de los platillos ya puestos en ese tiempo, que no vale repetir. */
  usedKeys?: Set<string>;
  limit?: number;
}

/**
 * Sugerencias que se muestran bajo el campo del platillo. Las del mismo tiempo
 * van primero (el "Fuerte" no debe ofrecer sopas), pero las de otros tiempos no
 * se esconden: un guisado puede cambiar de tiempo y la lista sigue sirviendo
 * como recetario.
 */
export function filterDishSuggestions({
  indexed,
  courseName,
  query,
  usedKeys,
  limit = 12,
}: DishSuggestionFilter): DailyMenuDishSuggestion[] {
  const courseKey = normalizeDishName(courseName);
  const queryKey = normalizeDishName(query);

  const matches = indexed.filter((entry) => {
    if (usedKeys?.has(entry.nameKey)) return false;
    return queryKey ? entry.nameKey.includes(queryKey) : true;
  });

  // Partición estable: el orden que trae el backend (más usados primero) se
  // conserva dentro de cada grupo.
  const ordered = courseKey
    ? [
        ...matches.filter((e) => e.courseKey === courseKey),
        ...matches.filter((e) => e.courseKey !== courseKey),
      ]
    : matches;

  // Un mismo platillo puede venir de dos tiempos; se muestra una vez, en el
  // orden que ya quedó (o sea, primero el del tiempo que se está editando).
  const seen = new Set<string>();
  const result: DailyMenuDishSuggestion[] = [];
  for (const entry of ordered) {
    if (seen.has(entry.nameKey)) continue;
    seen.add(entry.nameKey);
    result.push(entry.suggestion);
    if (result.length === limit) break;
  }

  return result;
}
